import { createContext, type PropsWithChildren, useContext, useState } from 'react';
import { BUS_GLOBAL_LANGUAGE } from '../auth/bus-topics';
import { useBusTopic } from '../widgets-bus';
import type { SgwtTranslationMessages, SgwtTranslationType } from './types';

type SgwtTranslationProviderProps = PropsWithChildren & {
  messages: Record<string, SgwtTranslationMessages>;
};

export const SgwtTranslationContext = createContext<SgwtTranslationType | undefined>(undefined);

/**
 * Flatten the object to have only one level of key.
 * Thus, `{a: {b: 'x'}`} becomes `{'a.b': 'x'}`).
 */
function flattenObject(object: Record<string, any>, prefix = '') {
  return Object.keys(object).reduce((accumulator: Record<string, string>, key: string) => {
    const pre = prefix.length ? `${prefix}.` : '';
    let result = accumulator;
    if (typeof object[key] === 'object') {
      result = {
        ...result,
        ...flattenObject(object[key], pre + key),
      };
    } else {
      result[pre + key] = object[key];
    }
    return result;
  }, {});
}

export function SgwtTranslationProvider({ children, messages: initialMessages }: SgwtTranslationProviderProps) {
  const [messages, setMessages] = useState<Record<string, SgwtTranslationMessages>>(initialMessages);
  const currentLanguage = useBusTopic<string | undefined>(BUS_GLOBAL_LANGUAGE) ?? 'en';

  // -------------- MANAGEMENT

  const addMessages = (language: string, ...listOfMessages: SgwtTranslationMessages[]) => {
    const newMessages = { ...messages };
    if (newMessages[language] === undefined) {
      newMessages[language] = {};
    }
    for (const messages of listOfMessages) {
      // We flatten the object (`{a: {b: 'x'}`} => `{'a.b': 'x'}`)
      const flattenMessages = flattenObject(messages);
      for (const key of Object.keys(flattenMessages)) {
        newMessages[language][key] = flattenMessages[key];
      }
    }
    setMessages(newMessages);
  };

  const availableLanguages = Object.keys(messages);
  const currentLanguageName = messages?.[currentLanguage]?.id ?? currentLanguage;

  // -------------- UTILITIES

  const translate = (key: string, values?: Record<string, string>): string => {
    let message = messages[currentLanguage][key] || key;
    // Replace placeholders in the message with values, e.g. "Hello {name}" with values { name: "John" }
    if (values) {
      for (const k of Object.keys(values)) {
        message = message.replace(new RegExp(`{${k}}`, 'g'), values[k]);
      }
    }
    return message;
  };

  return (
    <SgwtTranslationContext.Provider
      value={{
        addMessages,
        availableLanguages,
        currentLanguage,
        currentLanguageName,
        translate,
      }}
    >
      {children}
    </SgwtTranslationContext.Provider>
  );
}

export function useSgwtTranslation() {
  const context = useContext(SgwtTranslationContext);
  if (context === undefined) {
    throw new Error('useSgwtTranslation must be used within a SgwtTranslationProvider');
  }

  return context;
}
